import {
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Moon,
  Sun,
  type Icon,
} from '@phosphor-icons/react';
import { weekdayOf, WEEKDAY_SHORT, type Location, hourLabel } from '@shh/shared';
import type { WeatherDay, WeatherHour } from '@shh/ui';
import { useEffect, useState } from 'react';
import { locale } from '../lib/locale';
import { todayText } from '../i18n/today';
import { calendarText } from '../i18n/calendar';

/**
 * Weather for the rest of the day from Open-Meteo (free, no key, uses DWD models for Germany).
 * Only rounded city coordinates leave the device. Refreshed every 30 minutes.
 */
export interface Forecast {
  temp: number;
  icon: Icon;
  summary: string;
  hint?: string;
  /** For the resting wall's sky. */
  kind: WeatherKind;
  /** First hour today with a good chance of rain (HH:MM) – for the family hints. */
  rainFrom?: string;
  hours: WeatherHour[];
  /** Details sheet ("antippen"): the next 24 hours, 7 days, wind, UV, sun. */
  feelsLike?: number;
  wind?: number;
  uv?: number;
  sunrise?: string;
  sunset?: string;
  next24: WeatherHour[];
  days: WeatherDay[];
}

export type WeatherKind = 'clear' | 'cloudy' | 'rain' | 'snow' | 'fog' | 'storm';

const kindOf = (code: number): WeatherKind =>
  code >= 95
    ? 'storm'
    : code >= 71 && code <= 86
      ? 'snow'
      : code >= 51
        ? 'rain'
        : code >= 45
          ? 'fog'
          : code >= 3
            ? 'cloudy'
            : 'clear';

function iconFor(code: number, night: boolean): Icon {
  if (code >= 95) return CloudLightning;
  if (code >= 71 && code <= 86) return CloudSnow;
  if (code >= 51) return CloudRain;
  if (code >= 45) return CloudFog;
  if (code >= 3) return Cloud;
  if (code >= 1) return night ? Moon : CloudSun;
  return night ? Moon : Sun;
}

const describe = (code: number) => {
  const w = todayText.weather;
  return code >= 95
    ? w.storm
    : code >= 71 && code <= 86
      ? w.snow
      : code >= 51
        ? w.rain
        : code >= 45
          ? w.fog
          : code >= 3
            ? w.cloudy
            : code >= 1
              ? w.fair
              : w.sunny;
};

interface Raw {
  current: {
    temperature_2m: number;
    weather_code: number;
    is_day: number;
    apparent_temperature?: number;
    wind_speed_10m?: number;
  };
  hourly: {
    time: string[];
    temperature_2m: number[];
    precipitation_probability: number[];
    weather_code: number[];
    is_day: number[];
  };
  daily?: {
    time: string[];
    weather_code: number[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    precipitation_probability_max: number[];
    sunrise: string[];
    sunset: string[];
    uv_index_max: number[];
  };
}

const dayName = (date: string, today: string, i: number) =>
  date === today
    ? calendarText.today
    : i === 1
      ? calendarText.tomorrow
      : WEEKDAY_SHORT[locale][weekdayOf(date) - 1]!;

export function toForecast(raw: Raw, now: Date): Forecast {
  const nowHour = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:00`;
  const start =
    Math.max(
      0,
      raw.hourly.time.findIndex((t) => t >= nowHour),
    ) + 1;
  const hourAt = (i: number): WeatherHour => ({
    time: raw.hourly.time[i]!.slice(11, 16),
    temp: Math.round(raw.hourly.temperature_2m[i]!),
    icon: iconFor(raw.hourly.weather_code[i]!, raw.hourly.is_day[i] === 0),
    rain: raw.hourly.precipitation_probability[i] ?? 0,
  });
  const every = (step: number, count: number) =>
    Array.from({ length: count }, (_, k) => start + k * step)
      .filter((i) => i < raw.hourly.time.length)
      .map(hourAt);
  const hours = every(2, 6);
  const d = raw.daily;
  const today = nowHour.slice(0, 10);
  const days: WeatherDay[] = d
    ? d.time.map((date, i) => ({
        date,
        label: dayName(date, today, i),
        icon: iconFor(d.weather_code[i]!, false),
        summary: describe(d.weather_code[i]!),
        min: Math.round(d.temperature_2m_min[i]!),
        max: Math.round(d.temperature_2m_max[i]!),
        rain: d.precipitation_probability_max[i] ?? 0,
      }))
    : [];
  const rainy = hours.find((h) => h.rain >= 50);
  const temp = Math.round(raw.current.temperature_2m);
  const coldest = Math.min(temp, ...hours.map((h) => h.temp));
  return {
    temp,
    icon: iconFor(raw.current.weather_code, raw.current.is_day === 0),
    summary: rainy
      ? todayText.weather.rainFrom(hourLabel(rainy.time, locale))
      : todayText.weather.dry(describe(raw.current.weather_code)),
    hint: rainy
      ? todayText.weather.umbrella
      : coldest <= 3
        ? todayText.weather.hat
        : coldest < 10
          ? todayText.weather.jacket
          : undefined,
    kind: kindOf(raw.current.weather_code),
    rainFrom: rainy?.time,
    hours,
    feelsLike:
      raw.current.apparent_temperature === undefined
        ? undefined
        : Math.round(raw.current.apparent_temperature),
    wind:
      raw.current.wind_speed_10m === undefined ? undefined : Math.round(raw.current.wind_speed_10m),
    uv: d?.uv_index_max[0] === undefined ? undefined : Math.round(d.uv_index_max[0]),
    sunrise: d?.sunrise[0]?.slice(11, 16),
    sunset: d?.sunset[0]?.slice(11, 16),
    next24: every(3, 8),
    days,
  };
}

/** One request per place for everybody who asks (the dashboard and the header). */
const cache = new Map<string, { at: number; forecast: Forecast }>();
const FRESH = 10 * 60_000;

export function useWeather(location?: Location, timeZone = 'Europe/Berlin'): Forecast | undefined {
  const cached = location ? cache.get(`${location.latitude},${location.longitude}`) : undefined;
  const [forecast, setForecast] = useState<Forecast | undefined>(cached?.forecast);
  const lat = location?.latitude;
  const lon = location?.longitude;
  useEffect(() => {
    if (lat === undefined || lon === undefined) return;
    let alive = true;
    const key = `${lat},${lon}`;
    const load = async () => {
      const hit = cache.get(key);
      if (hit && Date.now() - hit.at < FRESH) {
        if (alive) setForecast(hit.forecast);
        return;
      }
      try {
        const url = `https://api.open-meteo.com/v1/forecast?${new URLSearchParams({
          latitude: String(lat),
          longitude: String(lon),
          current: 'temperature_2m,weather_code,is_day,apparent_temperature,wind_speed_10m',
          hourly: 'temperature_2m,precipitation_probability,weather_code,is_day',
          daily:
            'weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,sunrise,sunset,uv_index_max',
          forecast_days: '7',
          timezone: timeZone,
        })}`;
        const res = await fetch(url);
        if (!res.ok) return;
        const raw = (await res.json()) as Raw;
        const next = toForecast(raw, new Date());
        cache.set(key, { at: Date.now(), forecast: next });
        if (alive) setForecast(next);
      } catch {
        // offline: keep the last forecast
      }
    };
    void load();
    const t = window.setInterval(load, 30 * 60_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [lat, lon, timeZone]);
  return forecast;
}
